// Frontend/src/components/common/SegmentedTabs.tsx
//
// One tab control for the whole console.
//
// The issues page and the payments page had grown two different ones: a
// segmented control on a grey track with a white active pill, and a bordered
// white bar with a solid blue active rectangle. Same job, same place in the
// hierarchy, two visual languages — which is what makes an app feel like it
// was assembled rather than designed.
//
// This is the segmented-control version, because the alternative fills a
// large area with saturated brand colour for something that is navigation,
// not an action.
//
// Scrolls horizontally rather than wrapping: four or five tabs do not fit a
// 360px viewport, and a wrapped second row pushes the content below the fold.

import React from "react";

export type SegmentedTabItem<K extends string> = {
  key: K;
  label: string;
  icon?: React.ReactNode;
  /** Rendered as a small pill. Omit or pass 0 to hide. */
  count?: number;
  /** Colours the count pill when this tab is active. Defaults to brand. */
  tone?: { bg: string; fg: string };
};

export default function SegmentedTabs<K extends string>({
  items,
  value,
  onChange,
  ariaLabel,
}: {
  items: SegmentedTabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="inline-flex min-w-full gap-1 rounded-2xl bg-gray-100/80 p-1 sm:min-w-0"
      >
        {items.map((item) => {
          const active = item.key === value;
          const tone = item.tone ?? { bg: "#E7EBF8", fg: "#1B3480" };
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(item.key)}
              className={`flex flex-1 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-2 text-[13px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
                active
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {item.icon}
              {item.label}
              {!!item.count && (
                <span
                  className="rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums"
                  style={
                    active
                      ? { background: tone.bg, color: tone.fg }
                      : { background: "#FFFFFF", color: "#6B7280" }
                  }
                >
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
